/**
 * `storefrontPublic.*` catalog procedures (spec 24 R4).
 *
 * Listing summary shape shared across home rails, collection pages,
 * search results. All money is integer paise (stringified bigint).
 */
import { z } from "zod";
import { uuid, moneyPaise } from "../_common.js";
import {
  announcementBarSchema,
  heroSliderSchema,
  categoryGridSchema,
  productGridSchema,
  collectionRailSchema,
  promoBannerSchema,
  statsStripSchema,
  aboutBlockSchema,
  richTextSchema,
  imageTextSchema,
  testimonialsSchema,
  faqSchema,
  logoWallSchema,
  gallerySchema,
  featuredProductSchema,
  contactSchema,
  newsletterSchema,
  leadCaptureSchema,
  storeThemeSchema,
} from "../store-home.js";

const slug = z.string().min(1).max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

/** Listing summary — the shared shape for cards across all pages. */
export const listingSummary = z.object({
  slug: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  /**
   * One-line merchandising copy for the card and the search dropdown — the
   * listing's `short_desc`, falling back to the item's `description`. Null when
   * the seller has written neither; the card then renders name and pack alone.
   */
  shortDesc: z.string().nullable(),
  fabric: z.string().nullable(),
  gsm: z.string().nullable(),
  minPackInclGstPaise: moneyPaise.nullable(),
  preDiscountInclGstPaise: moneyPaise.nullable(),
  pieces: z.number().int().nullable(),
  packName: z.string().nullable(),
  moq: z.number().int().nullable(),
  discountBps: z.number().int(),
  /** Seller-maintained pack count for the primary entry; null = untracked.
   *  Drives the card's stock badge (In stock / Low stock / Sold out). */
  packsOnHand: z.number().int().nullable(),
  /**
   * True when this listing HAS a price but the caller isn't an authorized
   * buyer, so the server withheld it (spec 39 §9 — prices are a signed-in
   * privilege). The money fields are `null` in that case and the real figure
   * is never serialized, so it cannot be recovered from the DOM. The card
   * renders a mask that opens sign-in. `false` when a real price is present
   * (authorized) or the listing genuinely carries no price.
   */
  priceLocked: z.boolean(),
});

export type ListingSummary = z.infer<typeof listingSummary>;

/** Collection summary in home response. */
export const collectionSummary = z.object({
  slug: z.string(),
  name: z.string(),
  type: z.string(),
  imageUrl: z.string().nullable(),
  listingCount: z.number().int(),
  listings: z.array(listingSummary),
});

// ── Resolved sections (spec 42 R7) ─────────────────────────────

/** One tile in a `category_grid` — a visible collection (spec 42 R2). */
export const categoryTile = z.object({
  slug: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  listingCount: z.number().int(),
});

/**
 * A section with its data already attached.
 *
 * R7: the storefront makes **one** request and never re-derives a section's
 * contents client-side, so the server resolves each source here. Sections that
 * carry no catalog data (hero, promo, announcement, stats, about) are the
 * config shape unchanged — their content comes from the store profile, which
 * travels in `store`.
 *
 * A section whose data resolves to zero items is dropped server-side entirely
 * (R12), so consumers never receive an empty grid to hide.
 */
export const resolvedSectionSchema = z.discriminatedUnion("type", [
  announcementBarSchema,
  heroSliderSchema,
  categoryGridSchema.extend({ tiles: z.array(categoryTile) }),
  productGridSchema.extend({ listings: z.array(listingSummary) }),
  collectionRailSchema.extend({ rails: z.array(collectionSummary) }),
  promoBannerSchema,
  statsStripSchema,
  aboutBlockSchema,
  // Module 22 D3 — pure-config sections carry no catalog data, so they pass
  // through the resolver unchanged.
  richTextSchema,
  imageTextSchema,
  testimonialsSchema,
  faqSchema,
  logoWallSchema,
  gallerySchema,
  featuredProductSchema,
  contactSchema,
  newsletterSchema,
  leadCaptureSchema,
]);
export type ResolvedSection = z.infer<typeof resolvedSectionSchema>;

// ── getStoreHome ───────────────────────────────────────────────

export const getStoreHomeInput = z.object({ storeSlug: slug });

export const getStoreHomeOutput = z.object({
  store: z.object({
    name: z.string(),
    slug: z.string(),
    description: z.string().nullable(),
    // ── Storefront profile (spec 28 R1) ─────────────────────────
    // Nullable throughout: the hero, stats strip and about block each drop
    // row-by-row when unset rather than rendering a placeholder.
    coverImageUrl: z.string().nullable(),
    tagline: z.string().nullable(),
    about: z.string().nullable(),
    establishedYear: z.number().int().nullable(),
    teamSize: z.string().nullable(),
    monthlyCapacity: z.string().nullable(),
    /** From `storefront_store_contact_v` — drives the WhatsApp CTA (spec 25 R3). */
    phone: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    gstin: z.string().nullable(),
  }),
  /**
   * The store's home composition (spec 42) — **additive**. `collections` and
   * `uncollectedListings` below are unchanged and still feed spec 25's header
   * nav and spec 27's rails; nothing was removed when sections arrived.
   *
   * Already resolved server-side: the published version when there is one,
   * otherwise the default composition, with disabled sections and unknown
   * types dropped. Consumers render this array in order.
   */
  sections: z.array(resolvedSectionSchema),
  collections: z.array(collectionSummary),
  uncollectedListings: z.array(listingSummary),
  /** Per-store appearance (module 22 A5) — colours, font pairing, radius. */
  theme: storeThemeSchema,
});

// ── getCollection ──────────────────────────────────────────────

export const getCollectionInput = z.object({
  storeSlug: slug,
  collectionSlug: slug,
  cursor: z.string().optional(),
});

export const getCollectionOutput = z.object({
  collection: z.object({
    slug: z.string(),
    name: z.string(),
    type: z.string(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
  }),
  listings: z.array(listingSummary),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});

// ── getProduct ─────────────────────────────────────────────────

const pricingEntry = z.object({
  /**
   * Stable per-entry id, composite `"<listingId>:<packId>"` — computed at
   * read time (no column). `packId` alone is not unique across listings;
   * the composite is, and it parses back into exactly the two ids order
   * placement needs. Repointing an entry to a new pack *version* changes
   * the id, so a stale cart line resolves "unavailable", not repriced.
   */
  entryId: z.string(),
  packName: z.string().nullable(),
  composition: z.string().nullable(),
  pieces: z.number().int(),
  // Money is null when the caller is not an authorized buyer (spec 39 §9):
  // the pack's shape (name, pieces, composition, MOQ, stock) still renders so
  // the buyer sees the whole product — only the figures are withheld, and they
  // never reach the wire, so the DOM cannot be inspected for them.
  pricePaise: moneyPaise.nullable(),
  discountBps: z.number().int(),
  sellingInclGstPaise: moneyPaise.nullable(),
  /**
   * Struck "was" figure on the same tax basis as the selling price
   * (spec 13 R6a) — server-computed; null when there is no discount.
   */
  preDiscountInclGstPaise: moneyPaise.nullable(),
  moq: z.number().int().nullable(),
  /**
   * Seller-maintained pack count, exposed since 2026-08-05 as the storefront
   * quantity cap (stepper max). Still not stock — nothing reserves or
   * deducts it. `null` = untracked → UI caps at 9,999.
   */
  packsOnHand: z.number().int().nullable(),
});

export const getProductInput = z.object({
  storeSlug: slug,
  productSlug: slug,
});

export const getProductOutput = z.object({
  /** Listing uuid — the id half of every entry's composite `entryId`. */
  listingId: uuid,
  slug: z.string(),
  name: z.string(),
  sku: z.string().nullable(),
  hsn: z.string().nullable(),
  taxRateBps: z.number().int().nullable(),
  shortDesc: z.string().nullable(),
  longDesc: z.string().nullable(),
  images: z.array(z.string()),
  attributes: z.record(z.string(), z.string()),
  pricingEntries: z.array(pricingEntry),
  /** True when prices were withheld (anonymous caller) — the card renders a
   *  mask and ordering pops sign-in. False for an authorized buyer. */
  priceLocked: z.boolean(),
  collections: z.array(z.object({ slug: z.string(), name: z.string() })),
  store: z.object({ name: z.string(), slug: z.string() }),
  siblings: z.array(listingSummary),
  /**
   * Whether `siblings` came from the product's first visible collection
   * ("Also in {collection}") or the storewide newest-published fallback
   * ("More from this store") — the strip header depends on it.
   */
  siblingsFromCollection: z.boolean(),
});

// ── getQrProduct (spec 52, ADR-0066) ───────────────────────────

/**
 * The QR resolve's input: the opaque permalink token minted by
 * `storeCatalog.ensureQrToken`. 43 chars today (32 bytes base64url); the
 * range leaves room without admitting junk.
 */
export const getQrProductInput = z.object({
  token: z.string().min(20).max(64).regex(/^[A-Za-z0-9_-]+$/),
});

/**
 * One bundle on the QR page. Same server-computed money as `pricingEntry`
 * above, with ONE deliberate difference: `pricePaise`/`sellingInclGstPaise`
 * are **non-nullable** — the QR token is itself the price-reveal grant
 * (ADR-0066), the one exception to spec 39 §9. The mask never applies here,
 * so the contract says so.
 */
const qrPricingEntry = z.object({
  entryId: z.string(),
  packName: z.string().nullable(),
  composition: z.string().nullable(),
  pieces: z.number().int(),
  pricePaise: moneyPaise,
  discountBps: z.number().int(),
  sellingInclGstPaise: moneyPaise,
  /** Struck "was" figure incl. GST — null when there is no discount. */
  preDiscountInclGstPaise: moneyPaise.nullable(),
  moq: z.number().int().nullable(),
  packsOnHand: z.number().int().nullable(),
});

/** One variant row on the QR page. Qty is derived from bundles (ADR-0061);
 *  `null` = untracked, a different fact from 0 = out of stock. */
const qrVariant = z.object({
  attributeValues: z.record(z.string(), z.string()),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  qty: z.number().int().nullable(),
});

/**
 * Discriminated on `status`: a token whose listing is not published resolves
 * `unavailable` with just the store identity — the page degrades gracefully
 * instead of 404ing a printed code. An unknown/revoked token is NOT_FOUND at
 * the procedure, not a union arm.
 */
export const getQrProductOutput = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("unavailable"),
    store: z.object({ name: z.string(), slug: z.string() }),
  }),
  z.object({
    status: z.literal("ok"),
    store: z.object({ name: z.string(), slug: z.string() }),
    product: z.object({
      listingId: uuid,
      slug: z.string(),
      name: z.string(),
      sku: z.string().nullable(),
      hsn: z.string().nullable(),
      taxRateBps: z.number().int().nullable(),
      shortDesc: z.string().nullable(),
      longDesc: z.string().nullable(),
      images: z.array(z.string()),
      attributes: z.record(z.string(), z.string()),
      /** Axis definitions, ordered — the variants table's column order. */
      variantAttributes: z.array(
        z.object({ name: z.string(), values: z.array(z.string()) }),
      ),
      pricingEntries: z.array(qrPricingEntry),
      variants: z.array(qrVariant),
    }),
  }),
]);
export type GetQrProductOutput = z.infer<typeof getQrProductOutput>;

// ── getPage (module 22) ────────────────────────────────────────

export const getPageInput = z.object({
  storeSlug: slug,
  pageSlug: slug,
});

export const getPageOutput = z.object({
  page: z.object({
    slug: z.string(),
    title: z.string(),
    body: z.string(),
  }),
});

// ── searchListings ─────────────────────────────────────────────

export const searchListingsInput = z.object({
  storeSlug: slug,
  /**
   * The buyer's term, trimmed. **Optional** — omitted or empty means "browse
   * everything", the full published catalogue in the same shape (spec 29 R4).
   * The "< 2 characters is not a search" rule is a UI concern: the caller
   * debounces and short-circuits, so a 1-char call is still a valid request.
   */
  q: z.string().trim().max(200).optional(),
  /** Opaque page cursor from a previous `nextCursor`. Never in the URL. */
  cursor: z.string().optional(),
  /** Page size. The results page takes the default; the dropdown asks for 6. */
  limit: z.number().int().min(1).max(48).default(12),
  /**
   * Result ordering (spec 29 R7). Price sorts follow the displayed
   * incl.-GST figure and put priceless listings last; `discount` is
   * better-discount-first. A cursor is only meaningful under the sort that
   * minted it — callers keep the pair together.
   */
  sort: z.enum(["newest", "discount", "price-asc", "price-desc"]).default("newest"),
});

export const searchListingsOutput = z.object({
  items: z.array(listingSummary),
  total: z.number().int(),
  nextCursor: z.string().nullable(),
});

// ── priceEntries ───────────────────────────────────────────────

export const priceEntriesInput = z.object({
  storeSlug: slug,
  /** Listing slugs to reveal prices for — the card's own slug. */
  slugs: z.array(slug).min(1).max(60),
});

/**
 * The signed-in price reveal (spec 39 §9). Anonymous catalog reads omit prices
 * entirely; a registered buyer's browser calls this WITH its session cookie to
 * fetch the real figures for the listings it can see, keyed by slug. Returns
 * only what an authorized buyer may see — an anonymous call gets an empty list.
 */
export const priceEntriesOutput = z.object({
  prices: z.array(
    z.object({
      slug: z.string(),
      minPackInclGstPaise: moneyPaise.nullable(),
      preDiscountInclGstPaise: moneyPaise.nullable(),
    }),
  ),
});
